import { useCallback, useMemo, useRef, useState } from "react";
import { auth } from "../lib/firebase";
import { authenticatedFetch } from "../lib/api";
import { SaasInventory } from "../types";

// Browser SaaS/AI Inventory data hook (ENG-3096). Admin-only: fetches the caller's own tenant
// inventory from GET /business/saas-inventory (tenant resolved server-side from the Firebase token;
// the window is a clamped query param). Mirrors useRiskReport.ts's fetch/dedup/reset shape.
export function useSaasInventory() {
  const [inventory, setInventory] = useState<SaasInventory | null>(null);
  const [loadingInventory, setLoadingInventory] = useState(false);
  const [inventoryError, setInventoryError] = useState<string | null>(null);
  // ENG-3162: when the shown inventory was fetched. The page prints it next to the lag caveat — the
  // result can be up to ~20 minutes behind the extension, and the page never refetches the same
  // window on its own, so "as of when?" needs an answer on screen.
  const [fetchedAt, setFetchedAt] = useState<Date | null>(null);
  // The (window, group) this inventory was last loaded for — hoisted state persists across page
  // navigation, so a re-mount's fetch is a no-op when nothing changed. Set only on success, so a
  // failed fetch retries on the next visit. Mirrors useRiskReport's loadedKey.
  const loadedKey = useRef<string | null>(null);
  // Guard against out-of-order responses when the admin toggles the window: only the LATEST request
  // may apply, or a stale response would print the wrong window's inventory.
  const requestSeq = useRef(0);
  // The key currently being FETCHED — an in-flight request has not written loadedKey yet, so a
  // duplicate click during it must compare against where we are HEADED, not only where we have been.
  const pendingKey = useRef<string | null>(null);

  const fetchInventory = useCallback(async (windowDays: number, groupId: string | null = null) => {
    if (!auth.currentUser) return;
    const key = `${windowDays}|${groupId ?? ""}`;
    if ((pendingKey.current ?? loadedKey.current) === key) return; // no spinner, no round-trip
    const seq = ++requestSeq.current;
    pendingKey.current = key;
    setLoadingInventory(true);
    setInventoryError(null);
    try {
      const qs = new URLSearchParams({ window: String(windowDays) });
      if (groupId) qs.set("groupId", groupId);
      const response = await authenticatedFetch(`/business/saas-inventory?${qs.toString()}`);
      if (seq !== requestSeq.current) return; // superseded by a newer window selection
      if (response.ok) {
        const body = await response.json();
        if (seq !== requestSeq.current) return;
        setInventory(body);
        setFetchedAt(new Date());
        // ENG-3144: a degraded 200 (the query failed) is shown, but it is not a LOADED result — the
        // page tells the admin to revisit to retry, which only works if the next visit refetches.
        loadedKey.current = body?.degraded ? null : key;
      } else {
        setInventory(null);
        loadedKey.current = null;
        setInventoryError("Could not load the SaaS & AI inventory for your organization.");
      }
    } catch (err) {
      if (seq !== requestSeq.current) return;
      console.error("Error fetching SaaS inventory:", err);
      setInventory(null);
      loadedKey.current = null;
      setInventoryError("Could not load the SaaS & AI inventory for your organization.");
    } finally {
      if (seq === requestSeq.current) {
        pendingKey.current = null;
        setLoadingInventory(false);
      }
    }
  }, []);

  /** ENG-3144: fetch again for the same window/group, ignoring the loaded-key dedup. */
  const retryInventory = useCallback(
    (windowDays: number, groupId: string | null = null) => {
      loadedKey.current = null;
      pendingKey.current = null;
      return fetchInventory(windowDays, groupId);
    },
    [fetchInventory],
  );

  const reset = useCallback(() => {
    setInventory(null);
    setInventoryError(null);
    setFetchedAt(null);
    loadedKey.current = null;
    pendingKey.current = null;
  }, []);

  return useMemo(
    () => ({ inventory, loadingInventory, inventoryError, fetchedAt, fetchInventory, retryInventory, reset }),
    [inventory, loadingInventory, inventoryError, fetchedAt, fetchInventory, retryInventory, reset],
  );
}
